import type { FeatureWithIDs } from '@/overmind/effects/adapters/baseadapter';

import type {
  CompletedCalculationTactic,
  LoadFlowScenarioResults
} from '../../../..';
import type { ForStoringAttentionStatus } from '../../../../evaluation/AttentionStatusRepository';
import type { RulesProvider } from '../../../../evaluation/RulesProvider';
import { createAcLinesLoadFlowDetails } from '../elementDetais/acLinesLoadFlowDetails';
import { createEnergyConsumersLoadFlowDetails } from '../elementDetais/energyConsumersLoadFlowDetails';
import { createLvBusbarsLoadFlowDetails } from '../elementDetais/mvBusbarsLoadFlowDetails';
import { createPowerTransformersLoadFlowDetails } from '../elementDetais/powerTransformersLoadFlowDetails';
import type {
  AcLineLoadFlowDetails,
  BusbarLoadFlowDetails,
  EnergyConsumerLoadFlowDetails,
  GridElementsLoadFlowDetails,
  PowerTransformerLoadFlowDetails
} from '../gridElementLoadFlowDetails.types';
import { LowVoltageEvaluationEngineFactory } from './LowVoltageEvaluationEngineFactory';
import { Features } from '../../../../createGridElementCalculationSetExecutionDetails';

export function createLvLoadFlowDetails(
  features: Features,
  scenariosResults: LoadFlowScenarioResults[],
  rulesProvider: RulesProvider,
  calculationTactic: CompletedCalculationTactic,
  attentionStatusRepository: ForStoringAttentionStatus
): GridElementsLoadFlowDetails {
  const mergedLoadFlowScenariosResults = scenariosResults.flatMap(
    scenarioResults => scenarioResults.results
  );

  const evaluationEngineFactory = new LowVoltageEvaluationEngineFactory(
    rulesProvider
  );
  const anyScenarioInputs = scenariosResults.flatMap(
    scenarioResults => scenarioResults.inputs
  );

  const filteredScenarioLoadFlowInputs = scenariosResults[0].inputs;
  const energyConsumersDetails: EnergyConsumerLoadFlowDetails[] =
    createEnergyConsumersLoadFlowDetails(
      features,
      evaluationEngineFactory.createEnergyConsumerEvaluationEngine(),
      mergedLoadFlowScenariosResults,
      calculationTactic,
      attentionStatusRepository,
      filteredScenarioLoadFlowInputs
    );

  const busbarDetails: BusbarLoadFlowDetails[] = createLvBusbarsLoadFlowDetails(
    features,
    evaluationEngineFactory,
    mergedLoadFlowScenariosResults,
    calculationTactic,
    calculationTactic.calculationSetResult.grid.functional,
    attentionStatusRepository,
    filteredScenarioLoadFlowInputs
  );

  const acLinesDetails: AcLineLoadFlowDetails[] = createAcLinesLoadFlowDetails(
    features,
    evaluationEngineFactory.createAcLineSegmentEvaluationEngine(),
    mergedLoadFlowScenariosResults,
    calculationTactic.calculationSetResult.grid.functional,
    attentionStatusRepository
  );

  const functionalGrid = calculationTactic.calculationSetResult.grid.functional;

  const powerTransformersDetails: PowerTransformerLoadFlowDetails[] =
    createPowerTransformersLoadFlowDetails(
      features,
      evaluationEngineFactory.createPowerTransformerEvaluationEngine(),
      mergedLoadFlowScenariosResults,
      functionalGrid,
      anyScenarioInputs[0],
      attentionStatusRepository
    );

  return [
    ...energyConsumersDetails,
    ...acLinesDetails,
    ...powerTransformersDetails,
    ...busbarDetails
  ];
}
